/**
 * OffLabelAttestationCard — Engine 68 physician attestation surface.
 *
 * June 2026 — two upgrades:
 *   1. Risk-weighted tiers. The engine now marks each detected off-label drug
 *      HIGH-ALERT (attestation required) or ELEVATED (recommended); routine,
 *      low-litigation off-label uses are suppressed upstream and never reach
 *      this card. Each attestation shows its tier badge + rationale.
 *   2. Treating-clinician gate. The attestation + legal-PDF artifact is a
 *      paid physician feature. A professional clinician-verification gate
 *      wraps the flow — the physician verifies once, then all attestations
 *      unlock pre-filled with their identity. (Real NPI / medical-board
 *      identity verification activates post-funding; this preview uses
 *      clinician self-attestation.)
 *
 * NO LLM. Static questions, deterministic submission, append-only + hash-chained.
 */
import { useState } from 'react';
import axios from 'axios';
import { Scale, ShieldCheck, AlertOctagon, FileDown, Lock, BadgeCheck, Stethoscope, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLES = {
  high_alert: {
    badge: 'bg-red-600 text-white border-red-700',
    label: 'HIGH-ALERT · ATTESTATION REQUIRED',
    ring: 'border-red-400 bg-red-50/60',
  },
  elevated: {
    badge: 'bg-amber-500 text-white border-amber-600',
    label: 'ELEVATED · RECOMMENDED',
    ring: 'border-amber-400 bg-amber-50',
  },
};

function TierBadge({ tier }) {
  const s = TIER_STYLES[tier] || TIER_STYLES.elevated;
  return (
    <span
      data-testid={`offlabel-tier-badge-${tier}`}
      className={`inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded border ${s.badge}`}
    >
      {tier === 'high_alert' && <AlertOctagon className="w-3 h-3" />}
      {s.label}
    </span>
  );
}

function PromptCard({ encounterId, prompt, clinician }) {
  const [usedFor, setUsedFor] = useState('');
  const [evidence, setEvidence] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [consent, setConsent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [err, setErr] = useState(null);

  const tier = prompt.risk_tier || 'elevated';
  const tierStyle = TIER_STYLES[tier] || TIER_STYLES.elevated;

  const handleSubmit = async () => {
    setErr(null);
    if (!usedFor.trim() || !reasoning.trim() || consent === null) {
      setErr('Please complete the indication, risk-benefit reasoning, and the informed-consent choice.');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API}/api/offlabel/attest`, {
        encounterId,
        drug: prompt.drug,
        used_for: usedFor,
        evidence_cited: evidence,
        risk_benefit_reasoning: reasoning,
        patient_informed_consent: !!consent,
        physician_name: clinician.name,
        physician_credential: clinician.credential,
        physician_npi: clinician.npi || '',
        treating_clinician_attestation: true,
      });
      setSubmitted(resp.data.attestation);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="border-2 border-emerald-400 bg-emerald-50 rounded-lg p-4 mb-3" data-testid={`offlabel-attested-${prompt.drug}`}>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-emerald-700" />
          <span className="text-sm font-bold text-emerald-900">
            Attestation recorded · {prompt.drug}
          </span>
        </div>
        <div className="text-xs text-emerald-800 font-mono">
          ID {submitted.attestationId?.slice(0, 8)}… · hash {submitted.attestation_hash?.slice(0, 12)}…
        </div>
        <div className="text-[11px] text-emerald-700 mt-1 italic">
          Append-only, hash-chained, tamper-evident. Attested by {clinician.name}
          {clinician.credential ? `, ${clinician.credential}` : ''}. Defends the chart per Wyeth v. Levine (2009).
        </div>
      </div>
    );
  }

  return (
    <div className={`border-2 rounded-lg p-4 mb-3 ${tierStyle.ring}`} data-testid={`offlabel-card-${prompt.drug}`}>
      <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Off-Label Decision Capture · 60-second attestation</div>
          <div className="text-base font-bold text-slate-900 mt-0.5">{prompt.drug}</div>
        </div>
        <div className="flex items-center gap-2">
          <TierBadge tier={tier} />
          {prompt.boxed_warning && (
            <span className="flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 border border-red-300 px-2 py-0.5 rounded" data-testid={`offlabel-boxed-${prompt.drug}`}>
              <AlertOctagon className="w-3 h-3" /> FDA BOXED WARNING
            </span>
          )}
        </div>
      </div>

      {prompt.risk_rationale && (
        <div className="text-[11px] text-slate-700 mb-2 bg-white/70 border-l-2 border-slate-400 pl-2 py-1 italic" data-testid={`offlabel-rationale-${prompt.drug}`}>
          {prompt.risk_rationale}
        </div>
      )}

      <div className="text-xs text-slate-800 mb-3 bg-white/70 border-l-2 border-slate-400 pl-2 py-1">
        <strong>FDA-approved for:</strong> {(prompt.fda_approved_for || []).join(', ') || '—'}<br />
        <strong>Likely off-label use:</strong> {prompt.likely_offlabel_use ? `${prompt.likely_offlabel_use.use} (evidence level ${prompt.likely_offlabel_use.evidence_level})` : 'not auto-detected — please specify'}
        {prompt.boxed_warning_text && (
          <div className="mt-1 text-red-800"><strong>Boxed warning:</strong> {prompt.boxed_warning_text}</div>
        )}
      </div>

      <div className="space-y-2">
        <div>
          <label className="text-[11px] font-bold text-slate-900 block mb-0.5">1. Indication being treated *</label>
          <input type="text" value={usedFor} onChange={e => setUsedFor(e.target.value)}
            data-testid={`offlabel-used-for-${prompt.drug}`}
            placeholder="e.g., Generalized anxiety disorder"
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-900 block mb-0.5">2. Evidence cited (PubMed PMID, AHFS, Micromedex, etc.)</label>
          <input type="text" value={evidence} onChange={e => setEvidence(e.target.value)}
            data-testid={`offlabel-evidence-${prompt.drug}`}
            placeholder="e.g., PMID 32129558 Mason 2020 systematic review"
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-900 block mb-0.5">3. Risk-benefit reasoning *</label>
          <textarea value={reasoning} onChange={e => setReasoning(e.target.value)}
            data-testid={`offlabel-reasoning-${prompt.drug}`}
            placeholder="Why does the benefit for THIS patient likely exceed the risk? What alternatives were considered?"
            rows={3}
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white leading-relaxed" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-900 block mb-0.5">4. Informed consent *</label>
          <div className="flex gap-2 mt-1">
            <button onClick={() => setConsent(true)}
              data-testid={`offlabel-consent-yes-${prompt.drug}`}
              className={`text-[11px] px-3 py-0.5 rounded border ${consent === true ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-emerald-700 border-emerald-300'}`}>Patient informed</button>
            <button onClick={() => setConsent(false)}
              data-testid={`offlabel-consent-no-${prompt.drug}`}
              className={`text-[11px] px-3 py-0.5 rounded border ${consent === false ? 'bg-red-600 text-white border-red-700' : 'bg-white text-red-700 border-red-300'}`}>Not informed</button>
          </div>
        </div>
      </div>

      {err && <div className="text-xs text-red-700 mt-2 italic" data-testid={`offlabel-error-${prompt.drug}`}>{err}</div>}

      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] italic text-slate-600 max-w-[55%]">
          Attesting as <strong>{clinician.name}{clinician.credential ? `, ${clinician.credential}` : ''}</strong>
          {clinician.npi ? ` · NPI ${clinician.npi}` : ''}. Append-only · hash-chained · tamper-evident.
        </div>
        <button onClick={handleSubmit} disabled={submitting}
          data-testid={`offlabel-submit-${prompt.drug}`}
          className="text-xs font-bold px-4 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 transition-colors">
          {submitting ? 'Submitting…' : 'Submit attestation'}
        </button>
      </div>
    </div>
  );
}

/**
 * ClinicianGate — the paid-physician gate. Professional framing so investors
 * see Engine 68 as a sellable product. Real identity verification (NPI
 * Registry + state medical boards) activates post-funding; preview uses
 * clinician self-attestation.
 */
function ClinicianGate({ pendingCount, onVerified }) {
  const [name, setName] = useState('');
  const [credential, setCredential] = useState('MD');
  const [npi, setNpi] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [err, setErr] = useState(null);

  const handleUnlock = () => {
    setErr(null);
    if (!name.trim()) { setErr('Please enter the treating clinician’s full name.'); return; }
    if (!confirmed) { setErr('Please confirm you are the treating clinician for this encounter.'); return; }
    onVerified({ name: name.trim(), credential, npi: npi.trim() });
  };

  return (
    <div className="rounded-xl overflow-hidden border-2 border-slate-800 shadow-lg" data-testid="offlabel-clinician-gate">
      {/* Header ribbon */}
      <div className="bg-slate-900 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-amber-300" />
          <div>
            <div className="text-sm font-bold text-white">Physician Verification Required</div>
            <div className="text-[11px] text-slate-300">Off-label attestation is a licensed-clinician feature</div>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest text-slate-900 bg-amber-300 px-2.5 py-1 rounded-full" data-testid="offlabel-paid-feature-ribbon">
          <Sparkles className="w-3 h-3" /> Paid Physician Feature
        </span>
      </div>

      <div className="bg-white p-5">
        <p className="text-xs text-slate-700 leading-relaxed mb-4">
          <span className="font-bold text-slate-900">{pendingCount} off-label decision{pendingCount === 1 ? '' : 's'}</span> in
          this encounter require{pendingCount === 1 ? 's' : ''} a treating-clinician attestation. Each attestation is recorded as an
          append-only, hash-chained legal artifact that defends the chart per <em>Wyeth v. Levine</em> (2009) and <em>Buckman</em> (2001).
          Verify your identity to unlock the attestation workspace.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div className="sm:col-span-1">
            <label className="text-[11px] font-bold text-slate-800 block mb-0.5">Full name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              data-testid="offlabel-gate-name"
              placeholder="e.g., Dr. Priya Menon"
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 bg-white" />
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-800 block mb-0.5">Credential</label>
            <select value={credential} onChange={e => setCredential(e.target.value)}
              data-testid="offlabel-gate-credential"
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 bg-white">
              <option>MD</option><option>DO</option><option>MBBS</option><option>NP</option><option>PA</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-800 block mb-0.5">NPI / License # <span className="font-normal text-slate-400">(optional)</span></label>
            <input type="text" value={npi} onChange={e => setNpi(e.target.value)}
              data-testid="offlabel-gate-npi"
              placeholder="e.g., 1234567890"
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 bg-white" />
          </div>
        </div>

        <label className="flex items-start gap-2 text-xs text-slate-800 cursor-pointer mb-1" data-testid="offlabel-gate-confirm-label">
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
            data-testid="offlabel-gate-confirm"
            className="mt-0.5 w-4 h-4 accent-slate-900" />
          <span>I am the <strong>treating clinician</strong> for this encounter and I am authorized to record off-label decision attestations.</span>
        </label>

        {err && <div className="text-xs text-red-700 mt-2 italic" data-testid="offlabel-gate-error">{err}</div>}

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 italic max-w-[58%]">
            <BadgeCheck className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            Identity verification against the NPI Registry and state medical boards activates post-funding. This preview uses clinician self-attestation.
          </div>
          <button onClick={handleUnlock}
            data-testid="offlabel-gate-unlock"
            className="flex items-center gap-2 text-sm font-bold px-5 py-2 rounded-lg bg-slate-900 text-amber-50 border border-slate-700 hover:bg-slate-800 transition-colors">
            <Stethoscope className="w-4 h-4" /> Verify &amp; unlock attestation
          </button>
        </div>
      </div>
    </div>
  );
}

/** Locked preview of the pending drugs — visible behind the gate. */
function LockedPreview({ prompts }) {
  return (
    <div className="mt-3 relative" data-testid="offlabel-locked-preview" aria-hidden="true">
      <div className="space-y-2 blur-[3px] select-none pointer-events-none">
        {prompts.map((p, i) => (
          <div key={`${p.drug}-${i}`} className="flex items-center justify-between border-2 border-slate-200 rounded-lg px-4 py-3 bg-slate-50">
            <span className="text-sm font-bold text-slate-800">{p.drug}</span>
            <TierBadge tier={p.risk_tier || 'elevated'} />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600 bg-white/90 border border-slate-300 px-3 py-1 rounded-full shadow-sm">
          <Lock className="w-3.5 h-3.5" /> Verify to unlock
        </span>
      </div>
    </div>
  );
}

export default function OffLabelAttestationCard({ encounterId, offlabelAttestations }) {
  const [clinician, setClinician] = useState(null);

  if (!encounterId || !offlabelAttestations?.eligible || !offlabelAttestations?.prompts?.length) {
    return null;
  }

  const prompts = offlabelAttestations.prompts;
  const highAlert = prompts.filter(p => (p.risk_tier || 'elevated') === 'high_alert').length;

  return (
    <section className="bg-white rounded-lg border-2 border-slate-300 p-4" data-testid="offlabel-attestation-card">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Scale className="w-5 h-5 text-slate-700" />
        <h2 className="text-base font-bold text-slate-900">
          Off-Label Decision Capture ({offlabelAttestations.count})
        </h2>
        {highAlert > 0 && (
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-red-700 bg-red-100 border border-red-300 px-2 py-0.5 rounded" data-testid="offlabel-high-alert-count">
            {highAlert} high-alert
          </span>
        )}
      </div>
      <p className="text-xs text-slate-700 italic mb-3 leading-relaxed">
        Engine 68 detected drugs used outside their FDA-approved indication. Prioritization is <strong>risk-weighted</strong>:
        high-alert medications (opioids, anticoagulants, insulin, chemotherapy, antipsychotics) require a full attestation;
        routine, evidence-backed off-label uses are suppressed as informational. The attestation puts your contemporaneous
        risk-benefit reasoning in the chart — the single biggest determinant of malpractice defense.
      </p>

      {!clinician ? (
        <>
          <ClinicianGate pendingCount={prompts.length} onVerified={setClinician} />
          <LockedPreview prompts={prompts} />
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 bg-emerald-50 border border-emerald-300 rounded-lg px-3 py-2" data-testid="offlabel-verified-banner">
            <BadgeCheck className="w-4 h-4 text-emerald-700" />
            <span className="text-xs text-emerald-900">
              Verified as <strong>{clinician.name}{clinician.credential ? `, ${clinician.credential}` : ''}</strong>
              {clinician.npi ? ` · NPI ${clinician.npi}` : ''}. Attestation workspace unlocked.
            </span>
          </div>
          {prompts.map((p, i) => (
            <PromptCard key={`${p.drug}-${i}`} encounterId={encounterId} prompt={p} clinician={clinician} />
          ))}
          <MoatADownloadRow encounterId={encounterId} />
        </>
      )}
    </section>
  );
}

function MoatADownloadRow({ encounterId }) {
  const [opening, setOpening] = useState(false);
  const handleOpen = () => {
    setOpening(true);
    try {
      const url = `${API}/api/moats/a/${encounterId}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setTimeout(() => setOpening(false), 600);
    }
  };
  return (
    <div className="mt-4 pt-3 border-t border-slate-200" data-testid="moat-a-download-row">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] text-slate-800 leading-relaxed max-w-[62%]">
          <strong className="text-slate-950">Legal-ready PDF.</strong> Download a Federal Rule of Evidence 902(13)
          self-authenticating off-label decision-capture document for this encounter.
          Hash-chained, tamper-evident, includes Wyeth v. Levine and Buckman legal-authority blocks.
        </div>
        <button
          onClick={handleOpen}
          disabled={opening}
          data-testid="moat-a-download-button"
          className="flex items-center gap-2 text-xs font-bold px-4 py-2 rounded bg-slate-900 text-amber-50 border border-slate-700 hover:bg-slate-800 disabled:opacity-50 transition-colors"
          title="Open the FRE 902(13) off-label documentation in a new tab. Print to PDF from your browser."
        >
          <FileDown className="w-4 h-4" />
          {opening ? 'Opening…' : 'Download Off-Label Doc (Moat A)'}
        </button>
      </div>
    </div>
  );
}
